import { useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useDevice, useDevices } from '../api/queries'
import { DeviceLabel } from '../components/DeviceLabel'
import {
  LabelMediaPicker,
  LabelPageStyle,
  RollPrintHint,
  labelSheetClass,
  useLabelMedia
} from '../components/LabelPrint'
import { ErrorBox, Loading } from '../components/ui'

/**
 * Printvenlig side med mærkater.
 *
 * `/devices/:id/label` viser ét mærkat; `/devices/label?ids=1,2,3` viser flere,
 * så et helt anlæg kan mærkes op i én omgang. Antallet af kopier kan skrues op,
 * for et mærkat der sidder i et fugtigt teknikrum, holder ikke evigt - og så er
 * det rart at have en ekstra i mappen.
 *
 * Alt uden for selve mærkaterne forsvinder ved print (se @media print i styles.css).
 */
export function DeviceLabelPage() {
  const { id } = useParams()
  const [params] = useSearchParams()
  const [copies, setCopies] = useState(1)
  const [media, setMedia] = useLabelMedia()

  const singleId = Number(id)
  const idList = (params.get('ids') ?? '')
    .split(',')
    .map((value) => Number(value.trim()))
    .filter((value) => Number.isFinite(value) && value > 0)

  const single = useDevice(Number.isFinite(singleId) && singleId > 0 ? singleId : 0)
  const all = useDevices({ includeInactive: true })

  const isLoading = idList.length > 0 ? all.isLoading : single.isLoading
  const error = idList.length > 0 ? all.error : single.error

  // Titlen bliver til filnavnet, hvis nogen printer til PDF.
  useEffect(() => {
    const previous = document.title
    document.title = 'Enhedsmærkater'
    return () => {
      document.title = previous
    }
  }, [])

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />

  const devices =
    idList.length > 0
      ? (all.data ?? []).filter((device) => idList.includes(device.id))
      : single.data
        ? [single.data.device]
        : []

  if (devices.length === 0) {
    return <ErrorBox error={new Error('Ingen enheder at printe mærkater for.')} />
  }

  // Hver enhed gentages det valgte antal gange.
  const sheet = devices.flatMap((device) =>
    Array.from({ length: copies }, (_, copy) => ({ device, key: `${device.id}-${copy}` }))
  )

  return (
    <>
      <div className="row no-print" style={{ marginBottom: 16, flexWrap: 'wrap' }}>
        <Link className="btn btn--sm" to={devices.length === 1 ? `/devices/${devices[0].id}` : '/devices'}>
          Tilbage
        </Link>

        <label className="row small muted" style={{ gap: 6 }}>
          Kopier pr. enhed
          <input
            type="number"
            min={1}
            max={10}
            style={{ width: 70 }}
            value={copies}
            onChange={(event) => setCopies(Math.min(10, Math.max(1, Number(event.target.value) || 1)))}
          />
        </label>

        <LabelMediaPicker media={media} onChange={setMedia} />

        <button className="btn btn--primary btn--sm" onClick={() => window.print()}>
          Print
        </button>

        <span className="tiny muted">
          {sheet.length} mærkat{sheet.length === 1 ? '' : 'er'} · {media === 'roll62' ? 'ét pr. klip på 62 mm-rullen' : 'print på selvklæbende ark'}
        </span>
      </div>

      <LabelPageStyle media={media} />
      <RollPrintHint media={media} />

      <div className={labelSheetClass(media)}>
        {sheet.map((entry) => (
          <DeviceLabel key={entry.key} device={entry.device} />
        ))}
      </div>
    </>
  )
}
